import {useCallback, useEffect, useState, FocusEvent} from 'react';

import styled, {useTheme, DefaultTheme} from 'styled-components';
import {useDispatch} from 'react-redux';
import {Form, Field} from 'react-final-form';
import {FormApi} from 'final-form';

import {addBillingPermissionUser, getBillingPermissionsList, removeBillingPermissionUser} from '~/shared/services/bffService/api';
import {attachModal} from '~/shared/components/ReduxModal';
import {getLocalizationService} from '~/shared/services/localisationService';
import actions from '~/shared/store/actions';
import {media} from '~/shared/theme/media';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {body14Normal, body16Bold, headingXS, subheadingSBold} from '~/shared/theme/typography';
import PieCloseModalButton from '~/shared/components/Modals/styled/PieCloseModalButton';
import {BillingPermissionsListResponse, MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {flexColumn} from '~/shared/theme/FlexLayout';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';
import {createLogger} from '~/shared/logging/logging';
import {emailValidator} from '~/shared/utils/validators';
import PieButton from '~/shared/theme/pie/PieButton/PieButton';
import User from '~/shared/store/models/User/User';
import {handleRefreshToken} from '~/shared/services/auth/auth';
import {is401Error} from '~/shared/services/apiErrorService';
import checkCircleFilledIcon from '~/assets/images/checkCircleFilled.svg';
import StatusBar from '~/shared/components/StatusBar';
import {flipOnLTR} from '~/shared/theme/utils';
import {trackEvent} from '~/shared/services/analytics';

import alertCircleFilledIcon from './assets/alertCircleFilled.svg';
import minusCircleFilledIcon from './assets/minusCircleFilled.svg';

const logger = createLogger('BillingPermissionsModal');

const Root = styled(ClickOutsideDiv)`
  display: flex;
  flex-direction: column;

  &&& {
    width: 100vw;

    /* Use dynamic viewport height so mobile browser chrome (e.g. Chrome's address bar)
       shrinks the modal to the visible area instead of clipping the centered header off-screen. */
    height: 100dvh;
    border-radius: 0;
    padding-top: 0;
    padding-bottom: 8px;
  }

  &:first-child {
    background-color: ${({theme}) => theme.colors.surface};
  }

  ${media.minLargeTablet`
    &&& {
      width: 500px;
      height: inherit;
    }
    max-width: 500px;
    justify-content: space-between;
    border-radius: 2px;
    box-shadow: ${({theme}) => theme.shadows.shadow1};
  `}
`;

const Header = styled.div`
  height: 64px;
  width: 100%;
  padding: 16px 0;
  background-color: ${({theme}) => theme.colors.surface};
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  line-height: 16px;

  svg {
    ${({theme}) => `
      fill: ${theme.colors.text2};
    `}
  }
`;

const Title = styled.h3`
  ${subheadingSBold}
  color: ${({theme}) => theme.colors.contentDefault};
  text-align: center;
  width: 100%;
  ${media.minLargeTablet`
    ${headingXS}  
  `}
`;

const ListWrapper = styled.div`
  flex: 1;
  padding: 32px 16px 0 16px;
  overflow-x: hidden;
  ${media.minLargeTablet`
    padding: 32px;
    overflow-y: auto;
    max-height: 60vh;
  `}
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  flex-direction: column;
  display: flex;
  gap: 24px;
  margin-top: 32px;
  padding-bottom: 20px;
`;

const AddressItem = styled.li`
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 20px;
  flex: 1;
  justify-content: space-between;

  &:last-child {
    border-bottom: 0;
    padding: 0 0 1px 0;
  }
`;

const SubTitle = styled.h3`
  ${body16Bold}
`;

const CloseButton = styled(PieCloseModalButton)`
  top: 16px;
`;

const Content = styled.div`
  ${flexColumn}
`;

const SubTitle2 = styled(SubTitle).attrs({as: 'h4'})`
  ${body14Normal}
  color: rgba(0, 0, 0, 0.64);
`;

const FormComponent = styled.form`
  margin-top: 24px;

  .end-slot-wrapper {
    ${flipOnLTR`
      left: 4px;
    `}
  }
`;

const StyledRemoveButton = styled(PieButton).attrs({
  variant: 'outlineSmall',
})`
  height: 32px;
`;

const StyledFormPieInput = styled(FormPieInput)`
  border-radius: 50px;
`;

const ActionButton = styled(PieButton)`
  padding: 10px 16px;
`;

const ValidationError = styled.span`
  display: block;
  color: ${({theme}) => theme.colors.contentError};
  margin-top: 4px;
`;

const StyledStatusBar = styled(StatusBar)`
  margin-top: 24px;
`;

type FormData = {
  email: string;
};

enum statues {
  USER_ADD_SUCCESSFULLY = 'user_add_successfully',
  USER_ADD_FAILED = 'user_add_failed',
  USER_REMOVE_SUCCESSFULLY = 'user_remove_successfully',
  USER_REMOVE_FAILED = 'user_remove_failed',
}

type Colors = DefaultTheme['colors'];
type ColorKey = {[K in keyof Colors]: Colors[K] extends string ? K : never}[keyof Colors];

type StatusConfig = {
  translationKey: string;
  icon: string;
  bgColorKey: ColorKey;
};

const statusMap: Partial<Record<statues, StatusConfig>> = {
  [statues.USER_ADD_SUCCESSFULLY]: {
    translationKey: 'billing_permission_added_successfully',
    icon: checkCircleFilledIcon,
    bgColorKey: 'feedbackSupportPositiveTonal',
  },
  [statues.USER_ADD_FAILED]: {
    translationKey: 'billing_permission_added_failed',
    icon: alertCircleFilledIcon,
    bgColorKey: 'feedbackSupportErrorTonal',
  },
  [statues.USER_REMOVE_SUCCESSFULLY]: {
    translationKey: 'billing_permission_removed_successfully',
    icon: minusCircleFilledIcon,
    bgColorKey: 'supportInfoTonal',
  },
  [statues.USER_REMOVE_FAILED]: {
    translationKey: 'billing_permission_removed_failed',
    icon: alertCircleFilledIcon,
    bgColorKey: 'feedbackSupportErrorTonal',
  },
};

const BillingPermissionsModal = ({args: {moneycardId}}: {args: {moneycardId: MoneyCard['id']}}) => {
  const [list, setList] = useState<BillingPermissionsListResponse['linkedUsers']>([]);
  const [status, setStatus] = useState<statues | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();

  const {t} = getLocalizationService();
  const dispatch = useDispatch();

  const handleCloseModal = useCallback(() => {
    dispatch(actions.setCurrentModal(null));
  }, [dispatch]);

  const fetchBillingPermissionsList = useCallback(async () => {
    try {
      const responseList = await getBillingPermissionsList(moneycardId);
      setList(responseList.linkedUsers);
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, () => fetchBillingPermissionsList());
      }
    }
  }, [moneycardId]);

  useEffect(() => {
    fetchBillingPermissionsList();
  }, [fetchBillingPermissionsList]);

  const clearErrors = () => {
    setServerError(null);
    setStatus(null);
  };

  const onAddAuthorizedUser = async ({email}: FormData, form: FormApi<FormData>) => {
    try {
      setIsLoading(true);
      clearErrors();

      trackEvent('billingPermissions_added');
      await addBillingPermissionUser(moneycardId, email);
      await fetchBillingPermissionsList();
      setStatus(statues.USER_ADD_SUCCESSFULLY);
      form.reset();
    } catch (error: any) {
      if (is401Error(error)) {
        await handleRefreshToken(error, () => onAddAuthorizedUser({email}, form));
        return;
      }

      if (error.message) {
        setStatus(statues.USER_ADD_FAILED);
        setServerError(error.message);
      }

      logger.warn('Failed to add billing permission user or fetching the list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRemoveAuthorizedUser = async (userId: User['userId']) => {
    try {
      setServerError(null);
      setStatus(null);

      trackEvent('billingPermissions_removed');
      await removeBillingPermissionUser(moneycardId, userId);
      await fetchBillingPermissionsList();

      setStatus(statues.USER_REMOVE_SUCCESSFULLY);
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, () => onRemoveAuthorizedUser(userId));
        return;
      }

      setStatus(statues.USER_REMOVE_FAILED);
      logger.warn('Failed to remove billing permission user or fetching the list:', error);
    }
  };

  const statusConfig = status ? statusMap[status] : null;

  return (
    <Root onClickOutside={handleCloseModal}>
      <Header>
        <Title id="modal-title">
          {t('manage_billing_permissions')}
        </Title>
        <CloseButton onClick={handleCloseModal} />
      </Header>

      <ListWrapper>
        <Content>
          <SubTitle>{t('add_billing_authorized_user')}</SubTitle>
          <SubTitle2>{t('insert_email_address_to_add_billing_authorized_user')}</SubTitle2>
        </Content>
        <Form<{email: string}>
          onSubmit={onAddAuthorizedUser}
          render={({handleSubmit}) => (
            <FormComponent onSubmit={handleSubmit}>
              <Field
                name="email"
                validate={emailValidator}
                render={({input, meta}) => {
                  const hasValidationError = meta.touched && !!meta.error && !meta.active;
                  return (
                    <>
                      <StyledFormPieInput
                        {...input}
                        onFocus={(e: FocusEvent<HTMLElement>) => {
                          if (input.onFocus) {
                            input.onFocus(e);
                          }
                          clearErrors();
                        }}
                        id="email-field"
                        inputPlaceholder={t('email_3')}
                        aria-autocomplete="none"
                        aria-label={t('insert_email')}
                        data-test-id="email-field"
                        sideComponent={
                          <ActionButton
                            variant="primaryMedium"
                            type="submit"
                            disabled={!input.value || Boolean(emailValidator(input.value))}
                            isLoading={isLoading}
                          >
                            {t('add')}
                          </ActionButton>
                        }
                        hasError={!!serverError || hasValidationError}
                        removeFloatLabel
                      />
                      {hasValidationError && <ValidationError>{t(meta.error)}</ValidationError>}
                    </>
                  );
                }}
              />
            </FormComponent>
          )}
        />
        {statusConfig && (
          <StyledStatusBar
            bgColor={theme.colors[statusConfig.bgColorKey]}
            text={serverError || t(statusConfig.translationKey)}
            iconUrl={statusConfig.icon}
          />
        )}
        {Boolean(list.length) && (
          <List>
            {list.map(user => (
              <AddressItem key={user.userId}>
                {user.fullName}
                <StyledRemoveButton onClick={() => onRemoveAuthorizedUser(user.userId)}>{t('remove')}</StyledRemoveButton>
              </AddressItem>
            ))}
          </List>
        )}
      </ListWrapper>
      
    </Root>
  );
};

BillingPermissionsModal.hideCloseButton = true;

attachModal('billingPermissions', BillingPermissionsModal);

export default BillingPermissionsModal;
